import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  apiUrl,
  causallyOrderedMonotonicMs,
  CarId,
  CLOCK_CHANNEL,
  ClockAck,
  ClockMessage,
  ClockStateRequest,
  datasetQuery,
  FORMATION_START_MS,
  isClockAck,
  isCurrentClockStateResponse,
  isClockMessage,
  isClockStateRequest,
  monotonicMs,
  NETWORK_CLOCK_REQUEST_PREFIX,
  NetworkEvent,
  PLAYBACK_RATE,
  RACE_CLOCK_SENDER_ID,
  SHARED_END_MS,
  shouldAcceptClockMessage,
  shouldRespondToClockStateRequest,
  Topology,
  ViewerState,
} from "./protocol";
import "./styles.css";
import { VideoSynchronizer } from "./video-sync";
import { HlsVideoSource } from "./hls-video-source";
import {
  IosOnboardAudioBalanceController,
  isIosAudioSessionDevice,
  loadOnboardMicAttenuationDb,
  normalizeOnboardMicAttenuationDb,
  saveOnboardMicAttenuationDb,
} from "./onboard-audio-balance";
import {
  loadReplyAudioGainDb,
  normalizeReplyAudioGainDb,
  saveReplyAudioGainDb,
} from "./reply-audio-gain";
import {
  CAR_PRESENTATIONS, RacePresentation, RacePresentationContextSnapshot, RuntimeStatus,
} from "./race-presentation";
import { GoalNetworkView } from "./goal-network";
import { clampRaceTime, formatRaceTime, GOAL_RACE_TIME_MS } from "./race-screen-contract";
import { Stage2RaceShell } from "./stage2-race-shell";
import {
  EmotionMapPanel,
  EmotionMapRecord,
  EmotionMapSynchronizationSnapshot,
} from "./emotion-map";
import { RaceLinesPanel } from "./race-lines";
import { Stage2TelemetryPanel } from "./stage2-telemetry-panel";
import {
  Stage2TelemetryRecord, telemetryToConversationMotionContext,
} from "./stage2-telemetry-data";
import { BigCaption, ConversationCaption, ConversationPanel } from "./conversation-panel";
import { ConversationContext } from "./conversation-adapter";
import { buildConversationEmotionState } from "./conversation-emotion";
import { loadRaceReloadState, saveRaceReloadState } from "./race-reload-state";
import { AppLanguageProvider, useAppLanguage } from "./i18n";
import { localizedDriverName, localizedStoryText } from "./localized-race-content";
import {
  AccessGate, hlsMediaUrl, installManagedFetch, mediaUrl, requestViewingAccess,
  useViewingAccessActive,
} from "./access-ui";
import { MaintenanceNotice } from "./maintenance-notice";

installManagedFetch();

const CARS: CarId[] = ["car1", "car6", "car14"];
const ZERO_HASH = "0".repeat(64);
const VIDEO_START_MS: Record<CarId, number> = {
  car1: -1_726_433,
  car6: -1_623_767,
  car14: -428_633,
};
const MEDIA_TRIM_SECONDS: Record<CarId, number> = { car1: 0, car6: 0, car14: 0 };
// The first complete common observation is car1 tick 0 at 75 ms. Starting a
// paused viewer there avoids presenting the pre-sample interval as a data-load
// hang while preserving 0 ms as a valid explicit seek target.
const INITIAL_COMMON_OBSERVATION_MS = 75;

const initialState: ViewerState = {
  raceTimeMs: INITIAL_COMMON_OBSERVATION_MS,
  playState: "paused",
  carId: "car1",
  seekGeneration: 0,
  topologyHash: ZERO_HASH,
};

interface SharedClock {
  state: ViewerState;
  stateRef: React.MutableRefObject<ViewerState>;
  broadcast: (next: ViewerState, responseTo?: ClockStateRequest) => void;
  publish: (next: ViewerState) => void;
  replaceLocal: (next: ViewerState) => void;
}

// The media element and the shared clock change ownership together.  While a
// source change or seek is in flight, no media timestamp belongs to either the
// old or the requested clock identity.
export interface MediaClockIdentity {
  carId: string;
  seekGeneration: number;
}

export class MediaClockOwnership {
  private revision = 0;
  private owner: MediaClockIdentity | null = null;

  beginTransition(): number {
    this.owner = null;
    this.revision += 1;
    return this.revision;
  }

  commitTransition(revision: number, identity: MediaClockIdentity): boolean {
    if (revision !== this.revision) return false;
    this.owner = identity;
    return true;
  }

  invalidateTransition(revision: number): boolean {
    if (revision !== this.revision) return false;
    this.owner = null;
    this.revision += 1;
    return true;
  }

  canPublish(identity: MediaClockIdentity): boolean {
    return this.owner?.carId === identity.carId
      && this.owner.seekGeneration === identity.seekGeneration;
  }
}

function sameViewerRequest(left: ViewerState, right: ViewerState): boolean {
  return left.seekGeneration === right.seekGeneration
    && left.carId === right.carId
    && left.raceTimeMs === right.raceTimeMs
    && left.playState === right.playState;
}

function useSharedClock(
  receiverId: string,
  onAcceptedAck?: (ack: ClockAck) => void,
  startingState: ViewerState = initialState,
): SharedClock {
  const [state, setState] = useState<ViewerState>(startingState);
  const stateRef = useRef(state);
  const sequence = useRef(0);
  const requestSequence = useRef(0);
  const lastSequenceBySender = useRef(new Map<string, number>());
  const lastRequestSequenceBySender = useRef(new Map<string, number>());
  const channel = useMemo(() => new BroadcastChannel(CLOCK_CHANNEL), []);
  const requestSenderId = useMemo(() =>
    `${NETWORK_CLOCK_REQUEST_PREFIX}${globalThis.crypto.randomUUID()}`, []);

  const replaceLocal = useCallback((next: ViewerState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  const broadcast = useCallback((next: ViewerState, responseTo?: ClockStateRequest) => {
    const messageSequence = ++sequence.current;
    const message: ClockMessage = {
      schema_version: "fixed-point-clock-message/v1",
      sender_id: RACE_CLOCK_SENDER_ID,
      message_sequence: messageSequence,
      seek_generation: next.seekGeneration,
      sent_at_monotonic_ms: monotonicMs(),
      race_time_ms: next.raceTimeMs,
      play_state: next.playState,
      playback_rate: PLAYBACK_RATE,
      car_id: next.carId,
      topology_hash: next.topologyHash,
      ...(responseTo ? {
        response_to_request_sender_id: responseTo.sender_id,
        response_to_request_sequence: responseTo.request_sequence,
      } : {}),
    };
    lastSequenceBySender.current.set(message.sender_id, messageSequence);
    channel.postMessage(message);
  }, [channel]);

  const publish = useCallback((next: ViewerState) => {
    replaceLocal(next);
    broadcast(next);
  }, [broadcast, replaceLocal]);

  useEffect(() => {
    const receive = (event: MessageEvent<unknown>) => {
      if (isClockAck(event.data)) {
        if (event.data.accepted) onAcceptedAck?.(event.data);
        return;
      }
      if (isClockStateRequest(event.data)) {
        if (receiverId !== RACE_CLOCK_SENDER_ID) return;
        const request = event.data;
        const previousSequence = lastRequestSequenceBySender.current.get(request.sender_id) ?? -1;
        if (!shouldRespondToClockStateRequest(previousSequence, request)) return;
        lastRequestSequenceBySender.current.set(request.sender_id, request.request_sequence);
        broadcast(stateRef.current, request);
        return;
      }
      if (!isClockMessage(event.data)) return;
      const message = event.data;
      const currentRequestedSnapshot = isCurrentClockStateResponse(
        requestSenderId, requestSequence.current, message,
      );
      if (message.response_to_request_sender_id !== undefined && !currentRequestedSnapshot) return;
      // Separate renderer processes can expose sub-millisecond offsets between
      // their monotonic clocks. Preserve causal ordering at the channel
      // boundary without changing the sender timestamp or measured upper bound.
      const receivedAt = causallyOrderedMonotonicMs(monotonicMs(), message.sent_at_monotonic_ms);
      const current = stateRef.current;
      const previousSequence = lastSequenceBySender.current.get(message.sender_id) ?? -1;
      const accepted = receiverId !== RACE_CLOCK_SENDER_ID
        && (currentRequestedSnapshot || shouldAcceptClockMessage(current, previousSequence, message));
      if (accepted) {
        lastSequenceBySender.current.set(message.sender_id, message.message_sequence);
        replaceLocal({
          raceTimeMs: message.race_time_ms,
          playState: message.play_state,
          carId: message.car_id,
          seekGeneration: message.seek_generation,
          topologyHash: message.topology_hash,
        });
      }
      const ack: ClockAck = {
        schema_version: "fixed-point-clock-ack/v1",
        receiver_id: receiverId,
        original_sender_id: message.sender_id,
        message_sequence: message.message_sequence,
        seek_generation: message.seek_generation,
        received_at_monotonic_ms: receivedAt,
        applied_at_monotonic_ms: causallyOrderedMonotonicMs(monotonicMs(), receivedAt),
        accepted,
        race_time_ms: message.race_time_ms,
        play_state: message.play_state,
        playback_rate: message.playback_rate,
        car_id: message.car_id,
        topology_hash: message.topology_hash,
      };
      channel.postMessage(ack);
    };
    channel.addEventListener("message", receive);
    return () => {
      channel.removeEventListener("message", receive);
      channel.close();
    };
  }, [broadcast, channel, onAcceptedAck, receiverId, replaceLocal, requestSenderId]);

  useEffect(() => {
    if (receiverId === RACE_CLOCK_SENDER_ID) return;
    const requestCurrentState = () => {
      const request: ClockStateRequest = {
        schema_version: "fixed-point-clock-state-request/v1",
        sender_id: requestSenderId,
        request_sequence: ++requestSequence.current,
        sent_at_monotonic_ms: monotonicMs(),
      };
      channel.postMessage(request);
    };
    requestCurrentState();
    const timer = window.setInterval(requestCurrentState, 500);
    return () => window.clearInterval(timer);
  }, [channel, receiverId, requestSenderId]);

  return { state, stateRef, broadcast, publish, replaceLocal };
}

async function fetchTopology(carId: CarId, signal?: AbortSignal): Promise<Topology> {
  const response = await fetch(apiUrl("/api/topology", { car_id: carId }), {
    cache: "no-store",
    signal,
  });
  if (!response.ok) throw new Error(`topology request failed: ${response.status}`);
  return response.json() as Promise<Topology>;
}

function SharedStatus({ state }: { state: ViewerState }) {
  const { t, formatNumber } = useAppLanguage();
  return (
    <div className="shared-status" aria-label={t("status.shared")}>
      <div><span>{t("status.raceTime")}</span><strong data-testid="race-time-ms">{formatNumber(state.raceTimeMs)}</strong><small>ms</small></div>
      <div><span>{t("status.playState")}</span><strong data-testid="play-state">
        {t(state.playState === "playing" ? "status.playing" : "status.paused")}
      </strong></div>
      <div><span>{t("status.selectedCar")}</span><strong data-testid="selected-car-id">{state.carId}</strong></div>
      <div><span>{t("status.seekGeneration")}</span><strong>{formatNumber(state.seekGeneration)}</strong></div>
    </div>
  );
}

function RaceStoryOverlay({ context }: { context: RacePresentationContextSnapshot | null }) {
  const { language, t } = useAppLanguage();
  if (!context || context.activeStory.length === 0) return null;
  const labels = new Map(context.raceEvents.map(event => [event.event_id, event.label_ja]));
  const active = context.activeStory.slice(0, 2);
  return (
    <aside
      className="stage2-story-overlay"
      data-testid="stage2-story-overlay"
      data-evidence-ids={active.map(item => item.evidence_id).join(",")}
      aria-label={t("story.title")}
    >
      <span>{t("story.title")}</span>
      {active.map(item => {
        const copy = localizedStoryText(
          item.evidence_id, language, labels.get(item.evidence_id) ?? t("story.current"), item.text_ja,
        );
        return <article key={item.evidence_id} data-evidence-id={item.evidence_id}>
          <strong>{copy.title}</strong>
          <p>{copy.body}</p>
        </article>;
      })}
    </aside>
  );
}

const STORY_CONTEXT_HANDOFF_GRACE_MS = 2_000;

function OnboardAudioIcon({ muted }: { muted: boolean }) {
  return <svg
    className="stage2-volume-icon"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M4 9v6h4l5 4V5L8 9H4Z" />
    {muted
      ? <path d="m17 9 4 4m0-4-4 4" />
      : <>
        <path d="M16 9.5a4 4 0 0 1 0 5" />
        <path d="M18.5 7a7.5 7.5 0 0 1 0 10" />
      </>}
  </svg>;
}

function RaceScreen() {
  const { language, t } = useAppLanguage();
  const viewingAccessActive = useViewingAccessActive();
  const tRef = useRef(t);
  tRef.current = t;
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsVideoSource = useRef(new HlsVideoSource());
  const videoSynchronizer = useRef(new VideoSynchronizer({
    sourceMatches: (video, source) => hlsVideoSource.current.matches(video, source),
    loadSource: (video, source) => hlsVideoSource.current.load(video, source),
  }));
  const persistenceEnabled = !new URLSearchParams(window.location.search).has("acceptance_dataset");
  const stage2DataEnabled = !new URLSearchParams(window.location.search).has("acceptance_dataset");
  const reloadInitialState = useMemo(() => persistenceEnabled
    ? loadRaceReloadState(window.sessionStorage, initialState)
    : initialState, [persistenceEnabled]);
  const acceptVideoAcknowledgement = useCallback((ack: ClockAck) => {
    videoSynchronizer.current.acceptClockAcknowledgement(ack);
  }, []);
  const clock = useSharedClock("fixed-point-race-screen", acceptVideoAcknowledgement, reloadInitialState);
  const { state, stateRef, broadcast, publish, replaceLocal } = clock;
  const [seekText, setSeekText] = useState(String(reloadInitialState.raceTimeMs));
  const [seekDraftMs, setSeekDraftMs] = useState<number | null>(null);
  const [onboardAudioEnabled, setOnboardAudioEnabled] = useState(false);
  const [onboardAudioMutedByUser, setOnboardAudioMutedByUser] = useState(false);
  const [microphoneModeActive, setMicrophoneModeActive] = useState(false);
  const iosAudioBalanceEnabled = useMemo(() => isIosAudioSessionDevice(
    window.navigator.userAgent,
    window.navigator.platform,
    window.navigator.maxTouchPoints,
  ), []);
  const [microphoneAttenuationDb, setMicrophoneAttenuationDb] = useState(() =>
    loadOnboardMicAttenuationDb(window.localStorage)
  );
  const [replyAudioGainDb, setReplyAudioGainDb] = useState(() =>
    loadReplyAudioGainDb(window.localStorage)
  );
  const onboardAudioBalance = useRef<IosOnboardAudioBalanceController | null>(null);
  // The conversation context is independent of video decoding. In the normal
  // viewer, start its sidecar requests immediately instead of waiting for the
  // first video frame. Acceptance datasets retain the old video-gated path.
  const [presentationDataEnabled, setPresentationDataEnabled] = useState(stage2DataEnabled);
  const [videoStatus, setVideoStatus] = useState<
    "preparing" | "seeking" | "waiting" | "stalled" | null
  >("preparing");
  const [mediaProfile, setMediaProfile] = useState<{
    hls: { available: boolean; release_id?: string; vehicles?: Record<CarId, {
      playlist_zero_race_time_ms: number;
    }> };
  } | null>(null);
  const [error, setError] = useState("");
  const [telemetryRecord, setTelemetryRecord] = useState<Stage2TelemetryRecord | null>(null);
  const [emotionSnapshot, setEmotionSnapshot] = useState<{
    carId: CarId;
    seekGeneration: number;
    record: EmotionMapRecord;
    previousRecord: EmotionMapRecord | null;
    synchronization: EmotionMapSynchronizationSnapshot;
  } | null>(null);
  const emotionHistoryRef = useRef<EmotionMapRecord[]>([]);
  const [presentationContext, setPresentationContext] = useState<RacePresentationContextSnapshot | null>(null);
  const [caption, setCaption] = useState<ConversationCaption | null>(null);
  const [conversationEngaged, setConversationEngaged] = useState(false);
  const lastReadyConversationContext = useRef<ConversationContext | null>(null);
  const lastReadyStoryContext = useRef<RacePresentationContextSnapshot | null>(null);
  const intendedStateRef = useRef(reloadInitialState);
  const mediaClockOwnership = useRef(new MediaClockOwnership());

  const acceptEmotionRecord = useCallback((
    record: EmotionMapRecord | null,
    synchronization: EmotionMapSynchronizationSnapshot | null,
  ) => {
    if (!record || !synchronization) {
      emotionHistoryRef.current = [];
      setEmotionSnapshot(null);
      return;
    }
    const history = emotionHistoryRef.current.filter(item =>
      item.car_id === record.car_id && item.race_time_ms < record.race_time_ms
      && item.race_time_ms >= record.race_time_ms - 2_000);
    const previousRecord = [...history].reverse().find(item => item.race_time_ms <= record.race_time_ms - 900) ?? null;
    emotionHistoryRef.current = [...history, record];
    setEmotionSnapshot({
      carId: state.carId, seekGeneration: state.seekGeneration, record, previousRecord, synchronization,
    });
  }, [state.carId, state.seekGeneration]);

  useEffect(() => {
    emotionHistoryRef.current = [];
  }, [state.carId, state.seekGeneration]);

  useEffect(() => {
    setCaption(null);
  }, [state.carId, state.seekGeneration]);

  const conversationContext = useMemo<ConversationContext | null>(() => {
    if (!telemetryRecord || telemetryRecord.car_id !== state.carId
      || telemetryRecord.seek_generation !== state.seekGeneration
      || !emotionSnapshot || emotionSnapshot.carId !== state.carId
      || emotionSnapshot.seekGeneration !== state.seekGeneration
      || emotionSnapshot.record.car_id !== state.carId
      || !presentationContext || presentationContext.carId !== state.carId
      || presentationContext.seekGeneration !== state.seekGeneration) return null;
    const motion = telemetryToConversationMotionContext(telemetryRecord);
    const cells = (origin: EmotionMapRecord["driver"]["situation"], prefix: string) =>
      Object.entries(origin.cells).map(([name, value]) => ({ cell_id: `${prefix}/${name}`, value }));
    const map = emotionSnapshot.record;
    const synchronization = emotionSnapshot.synchronization;
    return {
      schema_version: "stage2-replay-conversation-context/v1",
      car_id: state.carId,
      race_time_ms: state.raceTimeMs,
      seek_generation: state.seekGeneration,
      driver_profile: {
        name_ja: CAR_PRESENTATIONS[state.carId].driver.replace(" ", ""),
        car_number: CAR_PRESENTATIONS[state.carId].number,
      },
      ...motion,
      pseudo_indicator_relative: presentationContext.pseudoIndicatorRelative,
      synchronization: {
        rate_percent: synchronization.ratePercent,
        raw_rate_percent: synchronization.measurement.raw_rate_percent,
        baseline_percent: 50,
        interpretation: synchronization.measurement.interpretation,
        source: "driver_and_vehicle_emotion_maps",
      },
      pseudo_indicators: presentationContext.pseudoIndicators,
      emotion_map: {
        driver: {
          situation: cells(map.driver.situation, "driver/situation"),
          reaction: cells(map.driver.reaction, "driver/reaction"),
        },
        vehicle: {
          situation: cells(map.vehicle.situation, "vehicle/situation"),
          reaction: cells(map.vehicle.reaction, "vehicle/reaction"),
        },
      },
      emotion_state: buildConversationEmotionState(map, emotionSnapshot.previousRecord),
      race_events: presentationContext.raceEvents,
      active_story: presentationContext.activeStory,
    };
  }, [emotionSnapshot, presentationContext, state.carId, state.raceTimeMs,
    state.seekGeneration, telemetryRecord]);

  // A presentation window advances once per second. Keep the last complete
  // context during the short hand-off to the next window so the microphone
  // does not alternate between ready and unavailable while the clock runs.
  const stableConversationContext = conversationContext
    ?? (lastReadyConversationContext.current?.car_id === state.carId
      && lastReadyConversationContext.current.seek_generation === state.seekGeneration
      ? lastReadyConversationContext.current : null);
  useEffect(() => {
    if (conversationContext) lastReadyConversationContext.current = conversationContext;
  }, [conversationContext]);

  // The one-second presentation window briefly becomes unavailable while the
  // next window is fetched. Retain the last complete story through only that
  // hand-off; a real new context with no active story still hides it at once.
  const retainedStoryContext = lastReadyStoryContext.current;
  const storyOverlayContext = presentationContext
    ?? (retainedStoryContext?.carId === state.carId
      && retainedStoryContext.seekGeneration === state.seekGeneration
      && state.raceTimeMs >= retainedStoryContext.raceTimeMs
      && state.raceTimeMs - retainedStoryContext.raceTimeMs <= STORY_CONTEXT_HANDOFF_GRACE_MS
      ? retainedStoryContext : null);
  useEffect(() => {
    if (presentationContext) lastReadyStoryContext.current = presentationContext;
  }, [presentationContext]);

  useEffect(() => {
    if (seekDraftMs === null) setSeekText(String(state.raceTimeMs));
  }, [seekDraftMs, state.raceTimeMs]);

  useEffect(() => {
    const controller = new AbortController();
    fetch(mediaUrl("/api/replay/media-profile"), { signal: controller.signal, cache: "no-store" })
      .then(async response => {
        if (!response.ok) throw new Error(`media profile HTTP ${response.status}`);
        return response.json();
      })
      .then(value => setMediaProfile(value))
      .catch(reason => {
        if (reason?.name === "AbortError") return;
        setMediaProfile({ hls: { available: false } });
        setError(`${tRef.current("error.videoPrepare")}: ${String(reason)}`);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    setPresentationDataEnabled(stage2DataEnabled && viewingAccessActive);
  }, [stage2DataEnabled, viewingAccessActive]);

  const mediaZeroRaceTimeMs = useCallback((carId: CarId) => {
    const hlsValue = mediaProfile?.hls.available
      ? mediaProfile.hls.vehicles?.[carId]?.playlist_zero_race_time_ms : undefined;
    return Number.isFinite(hlsValue) ? Number(hlsValue)
      : VIDEO_START_MS[carId] - MEDIA_TRIM_SECONDS[carId] * 1000;
  }, [mediaProfile]);

  const expectedVideoSeconds = useCallback((carId: CarId, raceTimeMs: number) =>
    (raceTimeMs - mediaZeroRaceTimeMs(carId)) / 1000, [mediaZeroRaceTimeMs]);

  const raceTimeFromVideo = useCallback((carId: CarId, videoTimeSeconds: number) =>
    Math.round(videoTimeSeconds * 1000 + mediaZeroRaceTimeMs(carId)), [mediaZeroRaceTimeMs]);

  const videoSource = useCallback((carId: CarId) => {
    const releaseId = mediaProfile?.hls.available ? mediaProfile.hls.release_id : undefined;
    return releaseId ? hlsMediaUrl(releaseId, carId) : mediaUrl(`/media/${carId}/onboard.mp4`);
  }, [mediaProfile]);

  const persistedTimeBucket = Math.floor(state.raceTimeMs / 250);
  useEffect(() => {
    if (!persistenceEnabled) return;
    saveRaceReloadState(window.sessionStorage, stateRef.current);
  }, [persistenceEnabled, persistedTimeBucket, state.carId, state.seekGeneration, stateRef]);

  useEffect(() => {
    if (!persistenceEnabled) return;
    const saveDisplayedPosition = () => {
      const current = stateRef.current;
      const video = videoRef.current;
      const raceTimeMs = current.playState === "playing" && video
        && mediaClockOwnership.current.canPublish(current)
        ? Math.min(GOAL_RACE_TIME_MS[current.carId], Math.max(FORMATION_START_MS,
          raceTimeFromVideo(current.carId, video.currentTime)))
        : current.raceTimeMs;
      saveRaceReloadState(window.sessionStorage, { ...current, raceTimeMs });
    };
    window.addEventListener("pagehide", saveDisplayedPosition);
    return () => window.removeEventListener("pagehide", saveDisplayedPosition);
  }, [persistenceEnabled, raceTimeFromVideo, stateRef]);

  const synchronizeVideo = useCallback(async (
    next: ViewerState,
    allowPlaybackFastPath = false,
  ): Promise<boolean> => {
    const video = videoRef.current;
    if (!video) return false;
    return videoSynchronizer.current.synchronize(video, {
      source: videoSource(next.carId),
      targetTimeSeconds: expectedVideoSeconds(next.carId, next.raceTimeMs),
      shouldPlay: next.playState === "playing",
      allowPlaybackFastPath,
      clockIdentity: {
        raceTimeMs: next.raceTimeMs,
        carId: next.carId,
        seekGeneration: next.seekGeneration,
      },
    });
  }, [expectedVideoSeconds, videoSource]);

  const publishImmediate = useCallback((next: ViewerState) => {
    intendedStateRef.current = next;
    publish(next);
  }, [publish]);

  const stopTransitionIfCurrent = useCallback((transitionRevision: number): boolean => {
    if (!mediaClockOwnership.current.invalidateTransition(transitionRevision)) return false;
    videoSynchronizer.current.cancel();
    videoRef.current?.pause();
    return true;
  }, []);

  const transitionWithVideo = useCallback(async (next: ViewerState): Promise<boolean> => {
    intendedStateRef.current = next;
    const transitionRevision = mediaClockOwnership.current.beginTransition();
    const displayed = stateRef.current;
    const allowPlaybackFastPath = next.carId === displayed.carId
      && next.seekGeneration === displayed.seekGeneration
      && next.playState !== displayed.playState;
    const synchronized = await synchronizeVideo(next, allowPlaybackFastPath);
    if (!synchronized) {
      stopTransitionIfCurrent(transitionRevision);
      return false;
    }
    const intended = intendedStateRef.current;
    const isCurrent = sameViewerRequest(intended, next);
    if (!isCurrent || !mediaClockOwnership.current.commitTransition(transitionRevision, next)) {
      stopTransitionIfCurrent(transitionRevision);
      return false;
    }
    replaceLocal(next);
    broadcast(next);
    const video = videoRef.current;
    if (video && next.playState === "paused") {
      if (!videoSynchronizer.current.armDisplayedHandoffPause(video)
        || !await videoSynchronizer.current.waitForPausedHandoff(video)) {
        stopTransitionIfCurrent(transitionRevision);
        return false;
      }
    }
    return true;
  }, [broadcast, replaceLocal, stateRef, stopTransitionIfCurrent, synchronizeVideo]);

  const recoverFromIncompleteTransition = useCallback(async (
    requested: ViewerState,
    operationLabel: string,
  ): Promise<boolean> => {
    // A newer request owns the media element.  The obsolete caller has handled
    // its false result by yielding without touching that newer owner.
    if (!sameViewerRequest(intendedStateRef.current, requested)) return true;
    const recovery = { ...stateRef.current, playState: "paused" as const };
    intendedStateRef.current = recovery;
    const recoveryRevision = mediaClockOwnership.current.beginTransition();
    try {
      const synchronized = await synchronizeVideo(recovery);
      if (!synchronized || !sameViewerRequest(intendedStateRef.current, recovery)
        || !mediaClockOwnership.current.commitTransition(recoveryRevision, recovery)) {
        const stopped = stopTransitionIfCurrent(recoveryRevision);
        if (stopped) setError(`${operationLabel}: ${tRef.current("error.videoSafeStopped")}.`);
        return false;
      }
      replaceLocal(recovery);
      broadcast(recovery);
      const video = videoRef.current;
      if (video && (!videoSynchronizer.current.armDisplayedHandoffPause(video)
        || !await videoSynchronizer.current.waitForPausedHandoff(video))) {
        const stopped = stopTransitionIfCurrent(recoveryRevision);
        if (stopped) setError(`${operationLabel}: ${tRef.current("error.videoSafeStopped")}.`);
        return false;
      }
      setError(`${operationLabel}: ${tRef.current("error.videoResynchronized")}.`);
      return true;
    } catch (reason) {
      const stopped = stopTransitionIfCurrent(recoveryRevision);
      if (stopped) setError(`${operationLabel}: ${tRef.current("error.videoSafeStopped")}: ${String(reason)}`);
      return false;
    }
  }, [broadcast, replaceLocal, stateRef, stopTransitionIfCurrent, synchronizeVideo]);

  useEffect(() => {
    const controller = new AbortController();
    if (!viewingAccessActive) return () => controller.abort();
    fetchTopology(state.carId, controller.signal).then(topology => {
      const current = stateRef.current;
      if (current.carId !== state.carId) return;
      const withTopology = { ...current, topologyHash: topology.topology_hash };
      intendedStateRef.current = { ...intendedStateRef.current, topologyHash: topology.topology_hash };
      replaceLocal(withTopology);
    }).catch(errorValue => {
      if (errorValue?.name !== "AbortError") setError(String(errorValue));
    });
    return () => controller.abort();
  }, [state.carId, stateRef, replaceLocal, viewingAccessActive]);

  useEffect(() => {
    const initial = stateRef.current;
    if (!mediaProfile || !viewingAccessActive) return;
    const transitionRevision = mediaClockOwnership.current.beginTransition();
    void synchronizeVideo(initial).then(async completed => {
      const video = videoRef.current;
      if (!completed || !mediaClockOwnership.current.commitTransition(transitionRevision, initial)) {
        stopTransitionIfCurrent(transitionRevision);
        await recoverFromIncompleteTransition(initial, tRef.current("operation.initialSync"));
        return;
      }
      if (video && stateRef.current.playState === "paused") {
        if (videoSynchronizer.current.armDisplayedHandoffPause(video)) {
          await videoSynchronizer.current.waitForPausedHandoff(video);
        }
      }
    }).catch(reason => setError(`${tRef.current("error.videoPrepare")}: ${String(reason)}`));
    return () => videoSynchronizer.current.cancel();
  }, [mediaProfile, recoverFromIncompleteTransition, stateRef, stopTransitionIfCurrent,
    synchronizeVideo, viewingAccessActive]);

  useEffect(() => () => hlsVideoSource.current.destroy(), []);

  useEffect(() => {
    if (state.playState !== "playing") return;
    const timer = window.setInterval(() => {
      const video = videoRef.current;
      const current = stateRef.current;
      if (!video || video.paused || current.playState !== "playing") return;
      if (!mediaClockOwnership.current.canPublish(current)) return;
      const goalRaceTimeMs = GOAL_RACE_TIME_MS[current.carId];
      const raceTimeMs = Math.min(
        goalRaceTimeMs,
        Math.max(FORMATION_START_MS, raceTimeFromVideo(current.carId, video.currentTime)),
      );
      publishImmediate({ ...current, raceTimeMs });
      if (raceTimeMs === goalRaceTimeMs) {
        video.pause();
        publishImmediate({ ...stateRef.current, playState: "paused" });
      }
    }, 50);
    return () => window.clearInterval(timer);
  }, [state.playState, publishImmediate, raceTimeFromVideo, stateRef]);

  useEffect(() => {
    if (state.playState !== "paused") return;
    const video = videoRef.current;
    if (video) videoSynchronizer.current.armDisplayedHandoffPause(video);
  }, [state.carId, state.playState, state.raceTimeMs, state.seekGeneration]);

  useEffect(() => {
    queueMicrotask(() => window.dispatchEvent(new CustomEvent("fixed-point-viewing-play-state", {
      detail: { playState: state.playState },
    })));
  }, [state.carId, state.playState, state.seekGeneration]);

  useEffect(() => {
    queueMicrotask(() => window.dispatchEvent(new CustomEvent(
      "fixed-point-viewing-conversation-engagement", { detail: { engaged: conversationEngaged } },
    )));
  }, [conversationEngaged]);

  useEffect(() => {
    let mismatchStartedAt: number | null = null;
    const timer = window.setInterval(() => {
      const video = videoRef.current;
      const current = stateRef.current;
      const pausedMismatch = Boolean(video
        && current.playState === "paused"
        && mediaClockOwnership.current.canPublish(current)
        && !videoSynchronizer.current.hasPendingPausedHandoff(video)
        && !video.paused);
      if (!pausedMismatch) {
        mismatchStartedAt = null;
        return;
      }
      mismatchStartedAt ??= performance.now();
      if (performance.now() - mismatchStartedAt < 750 || !video) return;
      videoSynchronizer.current.cancel();
      video.pause();
      video.currentTime = expectedVideoSeconds(current.carId, current.raceTimeMs);
      mismatchStartedAt = null;
      setError(`${t("error.videoClockMismatch")}.`);
    }, 100);
    return () => window.clearInterval(timer);
  }, [expectedVideoSeconds, stateRef, t]);

  const commitSeek = async (requestedValue?: number) => {
    const requested = requestedValue ?? Number(seekText);
    if (!Number.isInteger(requested) || requested < FORMATION_START_MS || requested > SHARED_END_MS) {
      setError(`${t("error.invalidRaceTime")} (${FORMATION_START_MS}–${SHARED_END_MS} ms).`);
      return;
    }
    if (!await requestViewingAccess()) return;
    setError("");
    const intended = intendedStateRef.current;
    const next = { ...intended, raceTimeMs: requested, seekGeneration: intended.seekGeneration + 1 };
    setSeekDraftMs(null);
    try {
      const completed = await transitionWithVideo(next);
      if (!completed) await recoverFromIncompleteTransition(next, t("operation.seek"));
    } catch (reason) {
      setError(`${t("error.videoAction")}: ${String(reason)}`);
    }
  };

  const switchCar = async (carId: CarId) => {
    if (!await requestViewingAccess()) return;
    setError("");
    const intended = intendedStateRef.current;
    const next = {
      ...intended,
      carId,
      seekGeneration: intended.seekGeneration + 1,
    };
    setSeekDraftMs(null);
    try {
      const completed = await transitionWithVideo(next);
      if (!completed) await recoverFromIncompleteTransition(next, t("operation.switchCar"));
    } catch (reason) {
      setError(`${t("error.videoAction")}: ${String(reason)}`);
    }
  };

  const togglePlay = async (requestedPlayState?: ViewerState["playState"]) => {
    const video = videoRef.current;
    if (!video) return;
    setError("");
    const intended = intendedStateRef.current;
    const displayed = stateRef.current;
    const ownsDisplayedVideo = mediaClockOwnership.current.canPublish(displayed);
    const displayedPausedHandoff = displayed.playState === "paused"
      && ownsDisplayedVideo
      && videoSynchronizer.current.hasExternallyObservedPausedHandoff(video);
    if (displayedPausedHandoff && requestedPlayState === undefined) {
      videoSynchronizer.current.cancel();
      video.pause();
      video.currentTime = expectedVideoSeconds(displayed.carId, displayed.raceTimeMs);
      publishImmediate(displayed);
      return;
    }
    if (requestedPlayState !== undefined && intended.playState === requestedPlayState) {
      broadcast(stateRef.current);
      return;
    }
    const nextPlayState = requestedPlayState
      ?? (intended.playState === "playing" ? "paused" as const : "playing" as const);
    if (nextPlayState === "playing" && !await requestViewingAccess()) return;
    setVideoStatus(nextPlayState === "playing" ? "preparing" : null);
    const raceTimeMs = intended.playState === "playing" && ownsDisplayedVideo
      ? Math.min(GOAL_RACE_TIME_MS[displayed.carId], Math.max(FORMATION_START_MS,
        raceTimeFromVideo(displayed.carId, video.currentTime)))
      : intended.raceTimeMs;
    try {
      const next = {
        ...intended,
        raceTimeMs,
        playState: nextPlayState,
      };
      const completed = await transitionWithVideo(next);
      if (!completed) await recoverFromIncompleteTransition(
        next, t(next.playState === "playing" ? "operation.play" : "operation.pause"),
      );
    } catch (reason) {
      setError(`${t("error.videoAction")}: ${String(reason)}`);
    }
  };

  const profile = CAR_PRESENTATIONS[state.carId];
  const sliderValue = seekDraftMs ?? clampRaceTime(state.carId, state.raceTimeMs);
  const commitSlider = (value: number) => {
    setSeekDraftMs(null);
    void commitSeek(value);
  };

  const enableOnboardAudio = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = onboardAudioMutedByUser;
      if (iosAudioBalanceEnabled) {
        const controller = onboardAudioBalance.current
          ?? new IosOnboardAudioBalanceController();
        onboardAudioBalance.current = controller;
        controller.setAttenuationDb(microphoneAttenuationDb);
        controller.setMicrophoneActive(microphoneModeActive);
        controller.enableFromUserGesture(video);
      }
    }
    setOnboardAudioEnabled(true);
  };

  const toggleOnboardAudioMute = () => {
    const nextMuted = !onboardAudioMutedByUser;
    const video = videoRef.current;
    if (video) video.muted = nextMuted;
    setOnboardAudioEnabled(true);
    setOnboardAudioMutedByUser(nextMuted);
  };

  useEffect(() => {
    if (!iosAudioBalanceEnabled) return;
    onboardAudioBalance.current?.setMicrophoneActive(microphoneModeActive);
  }, [iosAudioBalanceEnabled, microphoneModeActive]);

  useEffect(() => () => {
    onboardAudioBalance.current?.close();
    onboardAudioBalance.current = null;
  }, []);

  const updateMicrophoneAttenuationDb = (decibels: number) => {
    const normalized = normalizeOnboardMicAttenuationDb(decibels);
    setMicrophoneAttenuationDb(normalized);
    saveOnboardMicAttenuationDb(window.localStorage, normalized);
    onboardAudioBalance.current?.setAttenuationDb(normalized);
  };

  const updateReplyAudioGainDb = (decibels: number) => {
    const normalized = normalizeReplyAudioGainDb(decibels);
    setReplyAudioGainDb(normalized);
    saveReplyAudioGainDb(window.localStorage, normalized);
  };

  const localizedProfile = { ...profile, driver: localizedDriverName(state.carId, language) };
  const videoStatusCopy = videoStatus === "preparing" ? "動画を準備しています…"
    : videoStatus === "seeking" ? "シーク中です…"
      : videoStatus === "waiting" ? "映像データを待っています…"
        : videoStatus === "stalled" ? "通信が停滞しています。再開を待っています…"
          : "";
  return <Stage2RaceShell
    state={state}
    identity={localizedProfile}
    networkHref={`/network${window.location.search}`}
    status={<><SharedStatus state={state} /><RuntimeStatus /></>}
    conversationEngaged={conversationEngaged}
    video={<>
      <div className="panel-heading"><span>ONBOARD / {state.carId.toUpperCase()}</span><i className={state.playState === "playing" ? "live" : ""} /></div>
      <video
        ref={videoRef}
        data-testid="onboard-video"
        data-audio-enabled={onboardAudioEnabled ? "true" : "false"}
        preload="metadata"
        playsInline
        muted={!onboardAudioEnabled || onboardAudioMutedByUser}
        onLoadStart={() => setVideoStatus(
          intendedStateRef.current.playState === "playing" ? "preparing" : null,
        )}
        onLoadedData={() => {
          setPresentationDataEnabled(true);
          setVideoStatus(null);
        }}
        onCanPlay={() => {
          setPresentationDataEnabled(true);
          setVideoStatus(null);
        }}
        onSeeking={() => setVideoStatus(
          intendedStateRef.current.playState === "playing" ? "seeking" : null,
        )}
        onWaiting={event => {
          if (intendedStateRef.current.playState === "playing"
            && event.currentTarget.readyState < HTMLMediaElement.HAVE_FUTURE_DATA)
            setVideoStatus("waiting");
        }}
        onStalled={event => {
          if (intendedStateRef.current.playState === "playing"
            && event.currentTarget.readyState < HTMLMediaElement.HAVE_FUTURE_DATA)
            setVideoStatus("stalled");
        }}
        onPlaying={() => setVideoStatus(null)}
      />
      {intendedStateRef.current.playState === "playing" && videoStatus && <div
        data-testid="video-status-overlay"
        data-video-status={videoStatus}
        role="status"
        aria-live="polite"
        style={{
          position: "absolute", zIndex: 5, inset: 0, display: "grid", placeItems: "center",
          padding: "48px 20px 20px", color: "#fff", background: "rgba(2, 6, 7, .62)",
          fontWeight: 700, textAlign: "center", pointerEvents: "none",
        }}
      >{videoStatusCopy}</div>}
      <div className="stage2-video-hud" aria-hidden="true">
        <span>{profile.number}</span>
        <strong>{formatRaceTime(state.raceTimeMs)}</strong>
      </div>
    </>}
    controls={<section className="stage2-transport" aria-label={t("transport.controls")}>
      <div className="stage2-playback-buttons">
        <button className="stage2-play-button" data-testid="play-toggle" onClick={() => {
          if (state.playState === "paused") enableOnboardAudio();
          void togglePlay();
        }}>
          <span aria-hidden="true">{state.playState === "playing" ? "Ⅱ" : "▶"}</span>
          {state.playState === "playing" ? t("transport.pause") : t("transport.play")}
        </button>
        <button
          type="button"
          className="stage2-volume-button"
          data-testid="onboard-volume-toggle"
          aria-label={t(onboardAudioMutedByUser ? "transport.unmute" : "transport.mute")}
          aria-pressed={onboardAudioMutedByUser}
          title={t(onboardAudioMutedByUser ? "transport.unmute" : "transport.mute")}
          onClick={toggleOnboardAudioMute}
        >
          <OnboardAudioIcon muted={onboardAudioMutedByUser} />
        </button>
      </div>
      <label className="stage2-car-select" htmlFor="vehicle-select">
        <span>{t("transport.car")}</span>
        <select
          id="vehicle-select"
          data-testid="vehicle-select"
          data-seek-generation={state.seekGeneration}
          value={state.carId}
          onChange={event => void switchCar(event.target.value as CarId)}
        >
          {CARS.map(car => <option key={car} value={car}>
            {CAR_PRESENTATIONS[car].number} · {localizedDriverName(car, language)}
          </option>)}
        </select>
      </label>
      <div className="stage2-seek-control">
        <div><span>{formatRaceTime(sliderValue)}</span><small>{formatRaceTime(GOAL_RACE_TIME_MS[state.carId])}</small></div>
        <input
          type="range"
          min={FORMATION_START_MS}
          max={GOAL_RACE_TIME_MS[state.carId]}
          step="1"
          value={sliderValue}
          data-testid="race-seek-slider"
          aria-label={t("transport.seekLabel")}
          onChange={event => {
            const value = Number(event.currentTarget.value);
            setSeekDraftMs(value);
            setSeekText(String(value));
          }}
          onPointerUp={event => commitSlider(Number(event.currentTarget.value))}
          onKeyUp={event => {
            if (["ArrowLeft", "ArrowRight", "Home", "End", "PageUp", "PageDown"].includes(event.key))
              commitSlider(Number(event.currentTarget.value));
          }}
        />
      </div>
      <label className="stage2-precise-seek" htmlFor="seek-time-ms">
        <span>{t("transport.raceTime")} (ms)</span>
        <input
          id="seek-time-ms"
          data-testid="seek-time-ms"
          inputMode="numeric"
          value={seekText}
          onChange={event => {
            setSeekText(event.target.value);
            const value = Number(event.target.value);
            setSeekDraftMs(Number.isInteger(value) ? clampRaceTime(state.carId, value) : null);
          }}
          onKeyDown={event => {
            if (event.key === "Enter") void commitSeek();
          }}
        />
      </label>
      <button className="stage2-seek-button" data-testid="seek-commit" data-seek-generation={state.seekGeneration} onClick={() => void commitSeek()}>{t("transport.seek")}</button>
      {error && <p className="error" role="alert">{error}</p>}
    </section>}
    slots={stage2DataEnabled ? {
      storyOverlay: <RaceStoryOverlay context={storyOverlayContext} />,
      courseLines: <RaceLinesPanel
        carId={state.carId}
        raceTimeMs={state.raceTimeMs}
        seekGeneration={state.seekGeneration}
        telemetryRecord={telemetryRecord}
        playState={state.playState}
        dataFetchEnabled={viewingAccessActive}
      />,
      telemetry: <Stage2TelemetryPanel
        carId={state.carId}
        raceTimeMs={state.raceTimeMs}
        seekGeneration={state.seekGeneration}
        playState={state.playState}
        dataFetchEnabled={viewingAccessActive}
        onCurrentRecord={setTelemetryRecord}
      />,
      emotionMap: <EmotionMapPanel
        carId={state.carId}
        raceTimeMs={state.raceTimeMs}
        seekGeneration={state.seekGeneration}
        dataFetchEnabled={viewingAccessActive}
        onCurrentRecord={acceptEmotionRecord}
      />,
      conversation: <ConversationPanel
        carId={state.carId}
        raceTimeMs={state.raceTimeMs}
        seekGeneration={state.seekGeneration}
        context={stableConversationContext}
        audioMuted={onboardAudioMutedByUser}
        onCaptionChange={setCaption}
        onEngagementChange={setConversationEngaged}
        onMicrophoneModeChange={setMicrophoneModeActive}
        microphoneAttenuationDb={iosAudioBalanceEnabled ? microphoneAttenuationDb : undefined}
        onMicrophoneAttenuationChange={iosAudioBalanceEnabled
          ? updateMicrophoneAttenuationDb : undefined}
        replyAudioGainDb={iosAudioBalanceEnabled ? replyAudioGainDb : undefined}
        onReplyAudioGainChange={iosAudioBalanceEnabled
          ? updateReplyAudioGainDb : undefined}
      />,
      caption: <BigCaption caption={caption} />,
    } : {}}
    observation={<RacePresentation
      carId={state.carId}
      raceTimeMs={state.raceTimeMs}
      seekGeneration={state.seekGeneration}
      playState={state.playState}
      dataFetchEnabled={presentationDataEnabled}
      onContextChange={setPresentationContext}
    />}
  />;
}

const edgeIdentity = (branchId: string, sourceId: string, targetId: string, direction: string): string =>
  `${branchId}\u0000${sourceId}\u0000${targetId}\u0000${direction}`;

function NetworkScreen() {
  const { t } = useAppLanguage();
  const { state } = useSharedClock("fixed-point-network-screen");
  const [topology, setTopology] = useState<Topology | null>(null);
  const [topologyCar, setTopologyCar] = useState<CarId | null>(null);
  const [events, setEvents] = useState<NetworkEvent[]>([]);
  const [error, setError] = useState("");
  const requestToken = useRef(0);
  const dataset = datasetQuery();
  const eventIdentity = `${state.carId}|${state.seekGeneration}|${dataset ?? "runtime"}`;
  const previousEventIdentity = useRef(eventIdentity);
  const eventWindowKey = Math.floor(state.raceTimeMs / 1_000);
  const eventsByEdge = useMemo(() => {
    const index = new Map<string, NetworkEvent[]>();
    for (const event of events) {
      const key = edgeIdentity(event.branch_id, event.source_id, event.target_id, event.direction);
      const current = index.get(key);
      if (current) current.push(event);
      else index.set(key, [event]);
    }
    return index;
  }, [events]);
  const nodeLayers = useMemo(() => {
    const preferredOrder = ["layer.3", "layer.2", "layer.1"];
    const presentLayers = [...new Set(topology?.nodes.map(node => node.layer_id) ?? [])];
    const order = [
      ...preferredOrder.filter(layerId => presentLayers.includes(layerId)),
      ...presentLayers.filter(layerId => !preferredOrder.includes(layerId)),
    ];
    return order.map(layerId => ({
      layerId,
      nodes: topology?.nodes.filter(node => node.layer_id === layerId) ?? [],
    })).filter(layer => layer.nodes.length > 0);
  }, [topology]);

  useEffect(() => {
    const controller = new AbortController();
    setTopologyCar(null);
    fetchTopology(state.carId, controller.signal).then(value => {
      setTopology(value);
      setTopologyCar(state.carId);
      setError("");
    }).catch(errorValue => {
      if (errorValue?.name !== "AbortError") setError(String(errorValue));
    });
    return () => controller.abort();
  }, [state.carId, dataset]);

  useEffect(() => {
    if (previousEventIdentity.current === eventIdentity) return;
    previousEventIdentity.current = eventIdentity;
    requestToken.current += 1;
    setEvents([]);
  }, [eventIdentity]);

  useEffect(() => {
    const token = ++requestToken.current;
    const controller = new AbortController();
    if (!topology || topologyCar !== state.carId) return () => controller.abort();
    const fromMs = dataset === "pilot"
      ? FORMATION_START_MS
      : Math.max(FORMATION_START_MS, state.raceTimeMs - 6_000);
    const toMs = dataset === "pilot"
      ? SHARED_END_MS
      : Math.min(SHARED_END_MS, state.raceTimeMs + 6_000);
    const topologyEdges = new Set(topology.edges.map(edge =>
      edgeIdentity(edge.branch_id, edge.source_id, edge.target_id, edge.direction)));
    fetch(apiUrl("/api/network/events", {
      car_id: state.carId,
      seek_generation: state.seekGeneration,
      from_ms: fromMs,
      to_ms: toMs,
    }), { cache: "no-store", signal: controller.signal }).then(async response => {
      if (!response.ok) throw new Error(`network event request failed: ${response.status}`);
      const text = await response.text();
      const parsed = text.split("\n").filter(Boolean).map(line => JSON.parse(line) as NetworkEvent);
      const currentOnly = parsed.filter(event => event.car_id === state.carId
        && event.race_time_ms >= fromMs
        && event.race_time_ms <= toMs
        && (dataset === null || topologyEdges.has(edgeIdentity(event.branch_id, event.source_id, event.target_id, event.direction))));
      if (dataset !== null && currentOnly.length !== parsed.length) throw new Error("event/topology binding differs");
      if (requestToken.current === token && !controller.signal.aborted) setEvents(currentOnly);
    }).catch(errorValue => {
      if (errorValue?.name !== "AbortError" && requestToken.current === token) setError(String(errorValue));
    });
    return () => controller.abort();
  }, [state.carId, state.seekGeneration, eventWindowKey, topology, topologyCar, dataset]);

  return (
    <main className="network-shell">
      <header className="masthead">
        <div><p className="eyebrow">{t("network.live")}</p><h1>{t("network.title")}</h1></div>
        <a href={`/${window.location.search}`}>{t("network.raceView")} ↗</a>
      </header>
      <SharedStatus state={state} />
      <RuntimeStatus />
      {dataset === null ? <GoalNetworkView
        state={state}
        events={events}
        topology={topologyCar === state.carId ? topology : null}
        error={error}
      /> : <section className="network-panel" data-topology-hash={topology?.topology_hash ?? ""}>
        <div className="panel-heading">
          <span>TOPOLOGY / {topology?.topology_id ?? t("network.loading")}</span>
          <span>{events.length} {t("network.pulses")}</span>
        </div>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="network-canvas">
          <div className="nodes" aria-label={t("network.nodes")}>
            {nodeLayers.map(layer => (
              <section className="node-layer" data-layer-id={layer.layerId} key={layer.layerId}>
                <h2>{layer.layerId.toUpperCase()}</h2>
                {layer.nodes.map(node => (
                  <div
                    className={`node side-${node.side}`}
                    data-node-id={node.neuron_id}
                    key={node.neuron_id}
                  >
                    <span>{node.side}</span><strong>{node.neuron_id}</strong><small>{node.relative_level} AzpNeuron</small>
                  </div>
                ))}
              </section>
            ))}
          </div>
          <div className="edges" aria-label={t("network.edges")}>
            {topology?.edges.map(edge => {
              const pulses = eventsByEdge.get(edgeIdentity(edge.branch_id, edge.source_id, edge.target_id, edge.direction)) ?? [];
              return (
                <div className={`edge direction-${edge.direction}`} data-edge-id={edge.edge_id} key={edge.edge_id}>
                  <div className="edge-label"><b>{edge.edge_id}</b><span>{edge.source_id} → {edge.target_id}</span></div>
                  <div className="edge-line">
                    {pulses.map(event => (
                      <i
                        className="pulse"
                        data-testid="network-pulse"
                        data-event-id={event.event_id}
                        data-race-time-ms={event.race_time_ms}
                        data-seek-generation={event.seek_generation}
                        data-car-id={event.car_id}
                        data-direction={event.direction}
                        data-branch-id={event.branch_id}
                        data-source-id={event.source_id}
                        data-target-id={event.target_id}
                        key={`${event.event_id}|${state.seekGeneration}|${event.car_id}`}
                        title={event.event_id}
                      />
                    ))}
                  </div>
                  <small>{edge.direction}</small>
                </div>
              );
            })}
          </div>
        </div>
      </section>}
    </main>
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("root element is missing");
createRoot(root).render(<AppLanguageProvider><AccessGate>
  <MaintenanceNotice />
  {window.location.pathname === "/network" ? <NetworkScreen /> : <RaceScreen />}
</AccessGate></AppLanguageProvider>);
